// Replaces summernote-lite's built-in "Insert Image" dialog with a URL-only
// version matching the legacy AngularJS app's dialog: no file picker, plus
// "Alternate text", "Width (Max 250px)" and "Height (Max 250px)" fields.
// The stock ImageDialog module's internals aren't exposed for reuse, so this
// replaces the module entirely rather than patching it.
export function installImageDialogWithDimensions($: any): void
{
  const summernoteNS = $?.summernote;
  if (!summernoteNS)
  {
    return;
  }

  class ImageDialogWithDimensions
  {
    context: any;
    ui: any;
    $body: any;
    options: any;
    lang: any;
    $dialog: any;

    constructor(context: any)
    {
      this.context = context;
      this.ui = $.summernote.ui;
      this.$body = $(document.body);
      this.options = context.options;
      this.lang = this.options.langInfo;
    }

    initialize()
    {
      const id = this.options.id;
      const $container = this.options.dialogsInBody ? this.$body : this.options.container;

      const body = [
        '<div class="form-group">',
        `<label for="note-dialog-image-url-${id}">${this.lang.image.url}</label>`,
        `<input id="note-dialog-image-url-${id}" class="note-image-url" type="text"/>`,
        '</div>',
        '<div class="form-group">',
        `<label for="note-dialog-image-alt-${id}">Alternate text</label>`,
        `<input id="note-dialog-image-alt-${id}" class="note-image-alt" type="text"/>`,
        '</div>',
        '<div class="form-group">',
        `<label for="note-dialog-image-width-${id}">Width (Max 250px)</label>`,
        `<input id="note-dialog-image-width-${id}" class="note-image-width" type="number" min="0"/>`,
        '</div>',
        '<div class="form-group">',
        `<label for="note-dialog-image-height-${id}">Height (Max 250px)</label>`,
        `<input id="note-dialog-image-height-${id}" class="note-image-height" type="number" min="0"/>`,
        '</div>',
        '<p class="note-image-size-warning">',
        'Images larger than 250 px width and/or 250 px height may look different in the card.',
        '</p>',
      ].join('');

      const footer = [
        `<button href="#" type="button" class="note-btn note-btn-primary note-image-btn disabled" disabled>`,
        this.lang.image.insert,
        '</button>',
      ].join('');

      this.$dialog = this.ui
        .dialog({
          className: 'image-dialog',
          title: this.lang.image.insert,
          fade: this.options.dialogsFade,
          body,
          footer,
        })
        .render()
        .appendTo($container);
    }

    destroy()
    {
      this.ui.hideDialog(this.$dialog);
      this.$dialog.remove();
    }

    bindEnterKey($input: any, $btn: any)
    {
      $input.on('keypress', (event: any) =>
      {
        if (event.key === 'Enter')
        {
          event.preventDefault();
          $btn.trigger('click');
        }
      });
    }

    show()
    {
      this.context.invoke('editor.saveRange');
      this.showImageDialog()
        .then(({ url, alt, width, height }: { url: string; alt: string; width: string; height: string }) =>
        {
          this.ui.hideDialog(this.$dialog);
          this.context.invoke('editor.restoreRange');

          if (!url)
          {
            return;
          }

          if (this.options.callbacks.onImageLinkInsert)
          {
            this.context.triggerEvent('image.link.insert', url);
            return;
          }

          this.context.invoke('editor.insertImage', url, ($image: any) =>
          {
            if (alt)
            {
              $image.attr('alt', alt);
            }

            const numericWidth = parseInt(width, 10);
            const numericHeight = parseInt(height, 10);

            if (Number.isFinite(numericWidth) && numericWidth > 0)
            {
              $image.css('width', `${numericWidth}px`);
            }
            else
            {
              const $editable = this.context.layoutInfo.editable;
              $image.css('width', Math.min($editable.width(), $image.width()));
            }

            if (Number.isFinite(numericHeight) && numericHeight > 0)
            {
              $image.css('height', `${numericHeight}px`);
            }
          });
        })
        .fail(() =>
        {
          this.context.invoke('editor.restoreRange');
        });
    }

    showImageDialog()
    {
      return $.Deferred((deferred: any) =>
      {
        const $imageUrl = this.$dialog.find('.note-image-url');
        const $imageAlt = this.$dialog.find('.note-image-alt');
        const $imageWidth = this.$dialog.find('.note-image-width');
        const $imageHeight = this.$dialog.find('.note-image-height');
        const $imageBtn = this.$dialog.find('.note-image-btn');

        const sanitizeDigitsOnly = ($input: any) =>
        {
          $input.on('input', function (this: HTMLInputElement)
          {
            if (!/^\d*$/.test(this.value))
            {
              this.value = this.value.replace(/\D/g, '');
            }
          });
        };

        this.ui.onDialogShown(this.$dialog, () =>
        {
          this.context.triggerEvent('dialog.shown');

          $imageUrl
            .on('input paste propertychange', () =>
            {
              this.ui.toggleBtn($imageBtn, $imageUrl.val());
            })
            .val('');
          $imageAlt.val('');
          $imageWidth.val('');
          $imageHeight.val('');

          sanitizeDigitsOnly($imageWidth);
          sanitizeDigitsOnly($imageHeight);

          if (!$.summernote.env?.isSupportTouch)
          {
            $imageUrl.trigger('focus');
          }

          $imageBtn.on('click', (event: any) =>
          {
            event.preventDefault();
            deferred.resolve({
              url: $imageUrl.val(),
              alt: $imageAlt.val(),
              width: $imageWidth.val(),
              height: $imageHeight.val(),
            });
          });

          this.bindEnterKey($imageUrl, $imageBtn);
        });

        this.ui.onDialogHidden(this.$dialog, () =>
        {
          $imageUrl.off();
          $imageAlt.off();
          $imageWidth.off();
          $imageHeight.off();
          $imageBtn.off();
          if (deferred.state() === 'pending')
          {
            deferred.reject();
          }
        });

        this.ui.showDialog(this.$dialog);
      });
    }
  }

  summernoteNS.options.modules.imageDialog = ImageDialogWithDimensions;
}
